import { InMemoryFs } from "just-bash";
import { readGroupFileBytes } from "../storage/readGroupFileBytes.js";
import { writeGroupFile } from "../storage/writeGroupFile.js";
import { deleteGroupFile } from "../storage/deleteGroupFile.js";
import { listGroupFiles } from "../storage/listGroupFiles.js";
import { createGroupDirectory } from "../storage/createGroupDirectory.js";
import type { ShadowClawDatabase } from "../db/types.js";

export class ShadowClawFileSystem extends InMemoryFs {
  private db: ShadowClawDatabase;
  private groupId: string;
  private workspacePrefix: string;

  constructor(
    db: ShadowClawDatabase,
    groupId: string,
    files: Record<string, any>,
    workspacePrefix: string = "/home/user",
  ) {
    super(files);
    this.db = db;
    this.groupId = groupId;
    this.workspacePrefix = workspacePrefix;
  }

  override async appendFile(
    path: string,
    content: string | Uint8Array,
  ): Promise<void> {
    await super.appendFile(path, content);

    const dbPath = this._toDbPath(path);
    if (dbPath !== null) {
      const finalContent = await this._readPathContent(path);
      await writeGroupFile(this.db, this.groupId, dbPath, finalContent);
    }
  }

  // cp, mv, etc., might not call writeFile internally in InMemoryFs,
  // so we hook them just in case to sync the destination.

  override async cp(src: string, dest: string, options?: any): Promise<void> {
    await super.cp(src, dest, options);

    // dest should now exist in memory
    const dbDest = this._toDbPath(dest);
    if (dbDest !== null) {
      try {
        const stats = await super.stat(dest);
        if (!this._isDirectoryStat(stats)) {
          const finalContent = await this._readPathContent(dest);
          await writeGroupFile(this.db, this.groupId, dbDest, finalContent);
        }
      } catch {}
    }
  }

  override async mkdir(path: string, options?: any): Promise<void> {
    await super.mkdir(path, options);

    const dbPath = this._toDbPath(path);
    if (dbPath !== null && dbPath !== "") {
      await createGroupDirectory(this.db, this.groupId, dbPath);
    }
  }

  override async mv(src: string, dest: string): Promise<void> {
    await super.mv(src, dest);

    let persistedDestInWorkspace = false;
    let resolvedDestPath = dest;

    const dbDest = this._toDbPath(dest);
    if (dbDest !== null) {
      try {
        const destStats = await super.stat(dest);
        if (this._isDirectoryStat(destStats)) {
          const sourceName = this._basename(src);
          if (sourceName) {
            resolvedDestPath = this._joinPath(dest, sourceName);
          }
        }

        const finalStats = await super.stat(resolvedDestPath);
        if (!this._isDirectoryStat(finalStats)) {
          const dbResolvedDest = this._toDbPath(resolvedDestPath);
          if (dbResolvedDest !== null) {
            const finalContent = await this._readPathContent(resolvedDestPath);
            await writeGroupFile(
              this.db,
              this.groupId,
              dbResolvedDest,
              finalContent,
            );
            persistedDestInWorkspace = true;
          }
        }
      } catch {}
    }

    const dbSrc = this._toDbPath(src);
    if (dbSrc !== null) {
      try {
        if (dbDest === null || persistedDestInWorkspace) {
          await deleteGroupFile(this.db, this.groupId, dbSrc);
        }
      } catch {}
    }
  }

  override async rm(path: string, options?: any): Promise<void> {
    await super.rm(path, options);

    const dbPath = this._toDbPath(path);
    if (dbPath !== null) {
      try {
        // Best-effort removal of corresponding storage file.
        // OPFS handles directories, but we primarily care about saving/deleting files.
        // deleteGroupFile parses the path and removes the entry inside the directory.
        await deleteGroupFile(this.db, this.groupId, dbPath);
      } catch {
        // Ignore if it was a directory or didn't exist
      }
    }
  }

  // Hook all methods that mutate file system

  override async writeFile(
    path: string,
    content: string | Uint8Array,
    options?: any,
  ): Promise<void> {
    await super.writeFile(path, content, options);

    // Read final state from in-memory and write back to DB
    const dbPath = this._toDbPath(path);
    if (dbPath !== null) {
      const finalContent = await this._readPathContent(path);
      await writeGroupFile(this.db, this.groupId, dbPath, finalContent);
    }
  }

  private _basename(path: string): string {
    const parts = path.split("/").filter(Boolean);

    return parts[parts.length - 1] || "";
  }

  private _isDirectoryStat(stat: any): boolean {
    if (stat && typeof stat.isDirectory === "function") {
      return !!stat.isDirectory();
    }

    return !!stat?.isDirectory;
  }

  private _joinPath(dir: string, name: string): string {
    const normalizedDir = dir.replace(/\/+$/, "");
    if (!normalizedDir) {
      return `/${name}`;
    }

    return `${normalizedDir}/${name}`;
  }

  /**
   * Map from absolute fs path to database path.
   */
  private _toDbPath(path: string): string | null {
    if (path.startsWith(this.workspacePrefix + "/")) {
      return path.slice(this.workspacePrefix.length + 1);
    } else if (path === this.workspacePrefix) {
      return "";
    }

    return null;
  }

  private async _readPathContent(path: string): Promise<Uint8Array | string> {
    try {
      return await super.readFileBuffer(path);
    } catch {
      return await super.readFile(path);
    }
  }
}

/**
 * Initialize a file system instance loaded with the workspace's files.
 */
export async function createFileSystem(
  db: ShadowClawDatabase,
  groupId: string,
): Promise<ShadowClawFileSystem> {
  const files = {};
  const directories = new Set<string>();
  const workspacePrefix = "/home/user";

  async function traverse(dirPath: string): Promise<void> {
    const entries = await listGroupFiles(db, groupId, dirPath);
    for (const entry of entries) {
      if (entry.endsWith("/")) {
        const dirName = entry.slice(0, -1);
        const childPath = dirPath === "." ? dirName : `${dirPath}/${dirName}`;
        directories.add(childPath);
        await traverse(childPath);
      } else {
        const filePath = dirPath === "." ? entry : `${dirPath}/${entry}`;
        const fsPath = `${workspacePrefix}/${filePath}`;
        files[fsPath] = async () => {
          try {
            return await readGroupFileBytes(db, groupId, filePath);
          } catch {
            return "";
          }
        };
      }
    }
  }

  await traverse(".");
  const fs = new ShadowClawFileSystem(db, groupId, files, workspacePrefix);

  // Ensure empty directories are visible to shell commands (ls/cd/mv destination checks).
  for (const dirPath of [...directories].sort(
    (a, b) => a.split("/").length - b.split("/").length,
  )) {
    try {
      fs.mkdirSync(`${workspacePrefix}/${dirPath}`, { recursive: true });
    } catch {
      // Ignore directory re-creation races or malformed entries.
    }
  }

  return fs;
}
